import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { ConfirmComponent } from 'src/app/components/dialog/confirm/confirm.component';
import { IConfirmDialogData } from 'src/app/components/dialog/dialog.data';
import { ValidationService } from 'src/app/services/validation.service';
import { environment } from 'src/environments/environment';
import * as Constants from "../../../../constant/Constants";
import { ApiCallService } from 'src/app/api/service/api-call.service';

@Component({
  selector: 'app-fnol',
  templateUrl: './fnol.component.html',
  styleUrls: ['./fnol.component.css']
})
export class FnolComponent implements OnInit {
  
  // Url Params
  claim: string = 'Accidental_damage';  // Windscreen, Other, Accidental_damage, theft ... etc.
  token: string = "placeholder-value"; // to validate the public accesses.
  // ++++++++++

  url: string = environment.baseUrl + Constants.claimFnolURL;
  loading: boolean = false;

  minDate: Date;
  maxDate: Date;

  genericDialogData: IConfirmDialogData = {
    title: '',
    content: '',
    cancelButtonText: '',
    confirmButtonText: ''
  };

  requiredString: string = 'This field is required';

  formErrors:{[key:string]:string}= {
    location: '',
    date: '',
    time: '',
    description: '',
    otherDriver: '',
    driverName: '',
    injuries: '',
    othersInvolved: '',
    otherDetails: ''
  }
  validationMessages:{[key:string]:{[key:string]:string}}= {
    bank: {required: this.requiredString },
    location: {required: this.requiredString },
    date: {required: this.requiredString },
    time: {required: this.requiredString, futureTime: 'Incident time cannot be in the future' },
    description: {required: this.requiredString },
    otherDriver: {required: this.requiredString },
    driverName: {required: this.requiredString },
    injuries: {required: this.requiredString },
    othersInvolved: {required: this.requiredString },
    otherDetails: {required: this.requiredString }
  }

  // Step 1
  motorForm = this._formBuilder.group({
    location: ['', Validators.required],
    date: [moment(null), Validators.required],
    time: ['', [Validators.required, this.validateTime.bind(this)]],
    description: ['', Validators.required],
  });

  constructor(
    private route: ActivatedRoute,
    private _formBuilder:FormBuilder,
    private validationService:ValidationService,
    public dialog: MatDialog,
    private apiCallService: ApiCallService
  ) {
    const currentDate = new Date();
    const tempDate = new Date(currentDate.getTime());
    
    tempDate.setDate(currentDate.getDate() - 7); // min date validation
    this.minDate = tempDate;
    this.maxDate = currentDate;  
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params["token"] !== undefined ? params["token"] : Constants.tokenPlaceholderValue;

      if (params['claim'] !== undefined) {
        this.claim = params['claim'];
      }
    });

    this.motorForm.valueChanges.subscribe(() => {
      this.validationService.logValidationErrors(
        this.motorForm,
        this.formErrors,
        this.validationMessages
      )
    });
  }

  logErrors() {
    this.validationService.logValidationErrors(
      this.motorForm,
      this.formErrors,
      this.validationMessages
    )
  }

  validateTime(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const selectedDate = this.motorForm.get('date')?.value;
    if (!selectedDate) {
      return null;
    }
    const stringDate = selectedDate.format("MM-DD-YYYY")
    const now = new Date();
    const selectedDateTime = new Date(stringDate);
    const [hours, minutes] = control.value.split(':').map(Number);
    
    selectedDateTime.setHours(hours, minutes, 0, 0);

    return selectedDateTime > now ? { futureTime: true } : null;
  }
  onDateChange() {
    // Trigger time validation when date changes
    this.motorForm.get('time')?.updateValueAndValidity();
  }

  initDialogData(title: string, content: string, cancelButtonText: string, confirmButtonText: string) {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  };

  showGenericPopup(runFunction: ()=> void) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        runFunction();
      }
    });
  };

  // POST function
  postDataToWhatsapp = async () => {
    this.loading = true;
    try {
      const req = {
        location: this.motorForm.controls['location'].value,
        date: (this.motorForm.controls['date'].value)!.format("DD-MM-YYYY"),
        time: this.motorForm.controls['time'].value,
        description: this.motorForm.controls['description'].value,

        token: this.token,
        filled: true,
      };

      if (req.date && req.date != '')
        req.date = (this.motorForm.controls['date'].value)!.format("YYYY-MM-DD");
      if (req.date == 'Invalid date')
        req.date = '';

      // Update form data (POST)
      const response = await this.apiCallService.postRequest(
        `${this.url}`, req
      );
      
      if (!environment.production) console.log(":: Response data ::  ", response);

      if (response.error === false) {
        if (!environment.production) console.log("::: Post successful :::");
        // Navigate back to Whatsapp chat
        window.location.href = environment.whatsappRedirectURL;
      } else {
        if (!environment.production) console.log("::: Post failed :::", response.message);
        this.loading = false;

        this.initDialogData(
          'Submission Failed',
          'An error occurred while saving the form details',
          'cancel',
          'TRY AGAIN'
        );

        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      if (!environment.production) console.error('ERROR :: ', error)
      this.loading = false;

      this.initDialogData(
        'Submission Failed',
        'An unexpected error occurred',
        'cancel',
        'TRY AGAIN'
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  }

  submitForm(): void {
    this.initDialogData(
      'Confirm submission',
      'Please proceed only if you have verified that the form details are correct',
      'Back',
      'Proceed'
    );

    this.showGenericPopup(this.postDataToWhatsapp);
  }

  confirmCancel() {
    this.initDialogData(
      'Warning',
      'Are you sure you want to cancel the application?',
      'back',
      'Cancel application'
    );

    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () =>  {
    // Update session that form application was cancelled
    this.loading = true;

    const req = {
      // User cancelled
      token: this.token,
      filled: false,
    };

    try {
      const response = await this.apiCallService.postRequest(`${this.url}?token=${this.token}`, req);
      if (!environment.production) console.log(":: Response data ::  ", response);

      if (response.error === false) {
        // Navigate back to Whatsapp chat
        window.location.href = environment.whatsappRedirectURL;
        
      } else {
        if (!environment.production) console.log("::: Cancel Post failed :::", response.message);
        this.loading = false;

        this.initDialogData(
          'Failed',
          'An error occurred while attempting to cancel your application',
          'Cancel',
          'TRY AGAIN'
        );

        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      if (!environment.production) console.error('ERROR :: ', error);
      this.loading = false;

      // Navigate back to Whatsapp chat
      window.location.href = environment.whatsappRedirectURL;
    }
  }

}
